/**
 * Shared SSR head configuration for clawinstall marketing routes.
 *
 * Every clawinstall marketing route emits the same SEO surface — title,
 * description, Open Graph, Twitter card, and a canonical link — built from one
 * helper instead of copy-pasted per-route blocks. Brand-scoped OG tags
 * (`og:site_name`, `og:type`) come from the root head config, so they are NOT
 * duplicated here.
 *
 * {@link publicPageHead} layers the design pages' scoped stylesheet
 * ({@link publicStylesheetLink}) on top, so it is in the `<head>` the server
 * sends. The five design-board pages render the `clawinstall-public`
 * components from a `.lazy.tsx` half; a side-effect `import "./public.css"`
 * inside that lazy chunk only reaches the browser after the entry JS has booted
 * and the route chunk has loaded, and the SSR manifest never lists it (it only
 * attributes CSS to chunks TanStack's own splitter produced), so every full load
 * painted the raw markup and then jumped into place. A parser-encountered
 * `<link rel="stylesheet">` blocks first paint until the sheet is applied, so
 * the first frame is the styled frame. The token-styled legal pages use plain
 * {@link marketingHead} and never download the design sheet.
 *
 * @since 1.0.0
 */
import publicCss from "~/components/design/clawinstall-public/public.css?url";
import { BRAND_IDENTITIES, type BrandIdentity } from "~/config/domains.ts";
import type { HeadLink } from "~/config/head.ts";

/**
 * Minimal shape of a route `head()` context carrying the root `beforeLoad`
 * identity. `identity` is `BrandIdentity | undefined` (not just optional) so this
 * is assignable from TanStack's real `head()` context under
 * `exactOptionalPropertyTypes`, where the resolved context types identity as
 * `BrandIdentity | undefined`.
 */
export interface HeadCtxLike {
  readonly match?: { readonly context?: { readonly identity?: BrandIdentity | undefined } };
}

/**
 * Identity for a route `head()` from the root `beforeLoad` context; ClawInstall
 * when absent (unit tests, unknown hosts).
 *
 * @since 1.0.0
 */
export const identityFromHeadCtx = (ctx?: HeadCtxLike): BrandIdentity =>
  ctx?.match?.context?.identity ?? BRAND_IDENTITIES.clawinstall;

/** Input for {@link marketingHead}. */
export interface MarketingHeadInput {
  /** Public path, e.g. "/" or "/pricing" — no query, no trailing slash except "/". */
  path: string;
  title: string;
  description: string;
  /** Defaults to the ClawInstall identity so existing callers keep their output. */
  identity?: BrandIdentity;
}

/**
 * The scoped `clawinstall-public` design stylesheet as a render-blocking head
 * link (`?url` import → a hashed, immutable asset; ~3 KB brotli). Root links
 * (the app stylesheet) come first in the merged head, so the scoped sheet's
 * rules cascade after `globals.css` exactly as they did when the chunk loaded it.
 *
 * @since 1.0.0
 */
export const publicStylesheetLink: HeadLink = { rel: "stylesheet", href: publicCss };

/** Shared SSR head config for clawinstall marketing routes: title/description, OG, Twitter card, canonical. */
export const marketingHead = ({ description, identity, path, title }: MarketingHeadInput) => {
  const canonicalHost = (identity ?? BRAND_IDENTITIES.clawinstall).canonicalHost;
  const url = `https://${canonicalHost}${path === "/" ? "/" : path}`;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: url }],
  };
};

/**
 * SSR head for the five `clawinstall-public` design pages (`/`, `/pricing`,
 * `/compare`, `/faq`, `/security`): {@link marketingHead} plus the scoped
 * design stylesheet as the FIRST link, so it is a render-blocking `<link>` in
 * the head the server sends (see the module docs for why it must be).
 *
 * @since 1.0.0
 */
export const publicPageHead = (input: MarketingHeadInput) => {
  const head = marketingHead(input);
  return { ...head, links: [publicStylesheetLink, ...head.links] };
};
